import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import mx from "./mx.json";
import en from "./en.json";

const dictionaries = { mx, en };
const supported = ["mx", "en"];

const I18nContext = createContext(null);

function getLangFromPath(pathname) {
  const seg = pathname.split("/").filter(Boolean)[0];
  if (supported.includes(seg)) return seg;
  return null;
}

export function I18nProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const pathLang = getLangFromPath(location.pathname);
  const [lang, setLang] = useState(pathLang || "mx");

  useEffect(() => {
    if (pathLang && pathLang !== lang) setLang(pathLang);
  }, [pathLang, lang]);

  useEffect(() => {
    document.documentElement.lang = lang === "mx" ? "es-MX" : "en";
    try {
      document.title = dictionaries[lang].meta.title;
      const desc = document.querySelector('meta[name="description"]');
      if (desc) desc.setAttribute("content", dictionaries[lang].meta.description);
    } catch (_) {}
  }, [lang]);

  const t = useMemo(() => {
    const dict = dictionaries[lang];
    return (path) => {
      const parts = path.split(".");
      let cur = dict;
      for (const p of parts) {
        if (cur == null) return path;
        cur = cur[p];
      }
      return cur == null ? path : cur;
    };
  }, [lang]);

  const switchLang = (target) => {
    if (!supported.includes(target)) return;
    // Preserve scroll position when switching languages
    const scrollY = window.scrollY;
    const currentPath = location.pathname;
    const rest = currentPath.replace(/^\/(mx|en)/, "");
    const nextPath = `/${target}${rest || ""}`;
    setLang(target);
    navigate(nextPath, { state: { keepScroll: true } });
    // Restore scroll on next tick
    window.requestAnimationFrame(() => window.scrollTo(0, scrollY));
    try { localStorage.setItem("dinv_lang", target); } catch (_) {}
  };

  const value = { lang, t, switchLang };
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}

export function pathFor(lang, sub = "") {
  return `/${lang}${sub}`;
}
